/**
 * useWidgetLayoutSync · v2.7.17
 * ──────────────────────────────
 * Bidirectional sync between localStorage and the server-side
 * `/admin/me/widget-layout` endpoint.
 *
 * Sync rules:
 *   1. ON MOUNT (login):
 *        · GET server layout
 *        · If server has saved data AND localStorage is empty/older →
 *          hydrate localStorage from server (so a fresh browser sees
 *          the operator's existing layout)
 *        · If localStorage has data AND server is empty → push localStorage
 *          to server (first-time sync)
 *        · If both exist → take whichever is newer based on a local
 *          `updated_at` timestamp also stored in localStorage
 *
 *   2. ON CHANGE:
 *        · The four keys (`:active`, root order, `:vis`, `:size`) are
 *          watched via the `storage` event AND a manual broadcast
 *          (`widget-layout:changed`) emitted by `DraggableStatsGrid` /
 *          `AdminDashboardPage` whenever they mutate state.
 *        · Pushes to the server are debounced by 1.5 s so a rapid drag
 *          doesn't spam the API.
 *
 * The hook is purely opt-in — callers that don't want server sync
 * (e.g. unit tests) just don't mount it.
 */
import { useEffect, useRef } from "react";
import adminApi from "@/lib/adminApi";

const PREFIX = "rtg_admin_stats_order_v1";
const KEYS = {
  order:      `${PREFIX}`,
  active:     `${PREFIX}:active`,
  visibility: `${PREFIX}:vis`,
  sizes:      `${PREFIX}:size`,
  ts:         `${PREFIX}:ts`,        // local "last-modified" wall clock
};

const _read = (k, fallback) => {
  try {
    const raw = window.localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};

const _write = (k, v) => {
  try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ }
};

const _readLocalLayout = () => ({
  order:      _read(KEYS.order, null),
  active:     _read(KEYS.active, null),
  visibility: _read(KEYS.visibility, null),
  sizes:      _read(KEYS.sizes, null),
});

const _writeLocalLayout = (layout) => {
  if (Array.isArray(layout?.order))      _write(KEYS.order, layout.order);
  if (Array.isArray(layout?.active))     _write(KEYS.active, layout.active);
  if (layout?.visibility && typeof layout.visibility === "object") _write(KEYS.visibility, layout.visibility);
  if (layout?.sizes && typeof layout.sizes === "object")           _write(KEYS.sizes, layout.sizes);
};

const _hasAnyLocal = () => {
  const l = _readLocalLayout();
  return !!(l.order?.length || l.active?.length || (l.visibility && Object.keys(l.visibility).length) || (l.sizes && Object.keys(l.sizes).length));
};

export default function useWidgetLayoutSync({ enabled = true } = {}) {
  const debounceRef = useRef(null);
  const lastPushedRef = useRef(null);
  // v2.7.17 · Suppress change-event handling until the initial GET
  // completes — otherwise the AdminDashboardPage's mount-time useEffect
  // (which writes localStorage from React state defaults) would bump
  // KEYS.ts to NOW and beat any legitimate older-but-real server data
  // in the freshness comparison.
  const hydratedRef = useRef(false);

  // ---- INITIAL HYDRATION ---------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/me/widget-layout");
        if (cancelled) return;
        const serverLayout = data?.layout || {};
        const serverTs = data?.updated_at ? Date.parse(data.updated_at) : 0;
        const localTs = Number(window.localStorage.getItem(KEYS.ts)) || 0;
        const hasServer = !!(serverLayout.active?.length || serverLayout.order?.length || serverLayout.sizes || serverLayout.visibility);
        const hasLocal = _hasAnyLocal();

        // Treat "local has data but no recorded timestamp" as fresh defaults
        // (i.e. just-written by useState/useEffect on mount) → server wins
        // when comparing freshness. Real user interactions ALWAYS bump KEYS.ts.
        const localTsValid = localTs > 0;

        if (hasServer && (!hasLocal || !localTsValid || serverTs > localTs)) {
          // Server wins — hydrate localStorage.
          _writeLocalLayout(serverLayout);
          window.localStorage.setItem(KEYS.ts, String(serverTs || Date.now()));
          // Broadcast so React state in DraggableStatsGrid / AdminDashboardPage rehydrates.
          window.dispatchEvent(new CustomEvent("widget-layout:hydrated", { detail: serverLayout }));
        } else if (!hasServer && hasLocal && localTsValid) {
          // First-time sync — push real local mutations to server.
          _pushNow();
        }
      } catch { /* silent — keep localStorage as-is */ }
      finally {
        // Now allow change events to bump KEYS.ts + schedule pushes.
        hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ---- PUSH IMPLEMENTATION -------------------------------------------------
  const _pushNow = () => {
    const layout = _readLocalLayout();
    // Skip if identical to what we last pushed (cheap dedupe).
    const sig = JSON.stringify(layout);
    if (sig === lastPushedRef.current) return;
    lastPushedRef.current = sig;
    adminApi.put("/admin/me/widget-layout", {
      active:     layout.active     || undefined,
      order:      layout.order      || undefined,
      visibility: layout.visibility || undefined,
      sizes:      layout.sizes      || undefined,
    }).catch(() => { lastPushedRef.current = null; /* let next change retry */ });
    window.localStorage.setItem(KEYS.ts, String(Date.now()));
  };

  // ---- ON CHANGE → DEBOUNCED PUSH ------------------------------------------
  useEffect(() => {
    if (!enabled) return undefined;
    const schedulePush = () => {
      // Ignore mount-time writes until the initial GET has completed.
      if (!hydratedRef.current) return;
      // v2.7.17 · Bump the local timestamp NOW so the next reload's
      // "server vs. local" comparison treats the local layout as the
      // newest version. Without this, if the server already had an
      // older layout, it would incorrectly win the merge on reload
      // because we hadn't recorded the local mutation's wall-clock time.
      try { window.localStorage.setItem(KEYS.ts, String(Date.now())); } catch { /* noop */ }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => { _pushNow(); }, 1500);
    };
    // Watch all four layout localStorage keys + the manual change event
    // emitted by DraggableStatsGrid / AdminDashboardPage.
    const onStorage = (e) => {
      if (!e.key || !e.key.startsWith(PREFIX)) return;
      schedulePush();
    };
    const onChange = () => schedulePush();
    window.addEventListener("storage", onStorage);
    window.addEventListener("widget-layout:changed", onChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("widget-layout:changed", onChange);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
